// src/components/SeoHead.tsx
// Bijgewerkt voor Pijler 0C + Pijler 2B:
// - Gefaseerde hreflang: alleen ACTIVE_LANGUAGES (NL + EN nu, uitbreiden per fase)
// - lcpHint: optionele <link rel="preload"> voor LCP-elementen (font, afbeelding)
// - Overige hreflang-logica ongewijzigd

import { useEffect } from "react";

// Gefaseerde taal-lancering — alleen actieve talen krijgen hreflang
// Fase 2: voeg "de", "fr" toe na 200+ geïndexeerde pagina's
// Fase 3: voeg "es", "it", "pt" toe na bewezen traffic
// Fase 4: resterende talen na 6+ maanden
const ACTIVE_LANGUAGES = ["nl", "en"];

// Alle ondersteunde talen (voor toekomstig gebruik)
const SUPPORTED_LANGS = [
  "en",
  "nl",
  "de",
  "fr",
  "es",
  "it",
  "pt",
  "sv",
  "pl",
  "ja",
  "ko",
  "ru",
  "tr",
  "da",
  "no",
  "fi",
  "cs",
];

interface SeoHeadProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | object[];
  hreflangBaseUrl?: string;
  ogImage?: string;
  // Optioneel: LCP hint voor preload — gebruik voor kritieke afbeeldingen of webfonts
  // Voorbeeld: lcpHint={{ href: "/fonts/display.woff2", as: "font", type: "font/woff2" }}
  lcpHint?: { href: string; as: string; type?: string };
  // Optioneel: ISO-4217 currency code (e.g. "USD", "EUR") for SEO meta + JSON-LD priceCurrency hint
  priceCurrency?: string;
}

export default function SeoHead({
  title,
  description,
  canonicalUrl,
  noindex,
  jsonLd,
  hreflangBaseUrl,
  ogImage,
  lcpHint,
  priceCurrency,
}: SeoHeadProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const resolvedImage = ogImage || "https://inkvaluator.com/og-image.png";

    setMeta("description", description);
    setMeta("og:site_name", "Inkvaluator", "property");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");
    if (canonicalUrl) setMeta("og:url", canonicalUrl, "property");

    setMeta("og:image", resolvedImage, "property");
    setMeta("twitter:image", resolvedImage);
    setMeta("twitter:card", "summary_large_image");

    if (priceCurrency) {
      setMeta("og:price:currency", priceCurrency, "property");
      setMeta("product:price:currency", priceCurrency, "property");
    }

    if (noindex) {
      setMeta("robots", "noindex, follow");
    } else {
      const existing = document.querySelector('meta[name="robots"]');
      if (existing) existing.remove();
    }

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonicalUrl) {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", canonicalUrl);
    } else if (canonical) {
      canonical.remove();
    }

    // LCP preload hint — voeg toe als nog niet aanwezig
    const existingPreload = document.querySelector("link[data-lcp-preload]");
    if (lcpHint) {
      if (!existingPreload) {
        const preload = document.createElement("link");
        preload.setAttribute("rel", "preload");
        preload.setAttribute("href", lcpHint.href);
        preload.setAttribute("as", lcpHint.as);
        if (lcpHint.type) preload.setAttribute("type", lcpHint.type);
        if (lcpHint.as === "font") preload.setAttribute("crossorigin", "anonymous");
        preload.setAttribute("data-lcp-preload", "true");
        document.head.appendChild(preload);
      }
    } else if (existingPreload) {
      existingPreload.remove();
    }

    // Hreflang — alleen ACTIVE_LANGUAGES (gefaseerd per manifest)
    // Verwijder oude hreflang links
    document.querySelectorAll("link[data-hreflang]").forEach((el) => el.remove());

    if (hreflangBaseUrl && !noindex) {
      // Normaliseer: strip www, query params, trailing slash
      const cleanBase = hreflangBaseUrl
        .replace(/^https?:\/\/(www\.)?[^/]*/, "https://inkvaluator.com")
        .replace(/\?.*$/, "")
        .replace(/\/$/, "");

      // Extraheer pad
      let path = "";
      try {
        path = new URL(cleanBase).pathname;
      } catch {
        path = cleanBase.replace("https://inkvaluator.com", "");
      }

      // Strip bestaand /{lang}/ prefix
      const langPrefixRegex = new RegExp(`^/(${SUPPORTED_LANGS.join("|")})(\/|$)`);
      const barePath = path.replace(langPrefixRegex, "/").replace(/^\/\//, "/");

      // Alleen actieve talen in hreflang — geen crawl budget verspilling
      ACTIVE_LANGUAGES.forEach((lang) => {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", lang);
        const langPath =
          lang === "nl" ? `https://inkvaluator.com${barePath}` : `https://inkvaluator.com/${lang}${barePath}`;
        link.setAttribute("href", langPath);
        link.setAttribute("data-hreflang", "true");
        document.head.appendChild(link);
      });

      // x-default wijst altijd naar NL root
      const xDefault = document.createElement("link");
      xDefault.setAttribute("rel", "alternate");
      xDefault.setAttribute("hreflang", "x-default");
      xDefault.setAttribute("href", `https://inkvaluator.com${barePath}`);
      xDefault.setAttribute("data-hreflang", "true");
      document.head.appendChild(xDefault);
    }

    // JSON-LD structured data
    const existingLd = document.querySelector("script[data-seo-jsonld]");
    if (existingLd) existingLd.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-jsonld", "true");
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      // Cleanup bij unmount
      document.querySelectorAll("link[data-hreflang]").forEach((el) => el.remove());
      const ld = document.querySelector("script[data-seo-jsonld]");
      if (ld) ld.remove();
      const preload = document.querySelector("link[data-lcp-preload]");
      if (preload) preload.remove();
    };
  }, [title, description, canonicalUrl, noindex, jsonLd, hreflangBaseUrl, ogImage, lcpHint, priceCurrency]);

  return null;
}
